import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Suppliers.css';

function Suppliers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [publicSuppliers, setPublicSuppliers] = useState([]);
  const [activeTab, setActiveTab] = useState('my-suppliers'); // 'my-suppliers' or 'discover'
  const [searchPublicQuery, setSearchPublicQuery] = useState('');
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  
  // Wizard state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showUploadWizard, setShowUploadWizard] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [supplierInventory, setSupplierInventory] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [uploadStep, setUploadStep] = useState(1); // 1: Upload, 2: Preview & Map, 3: Success
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
    const [mapping, setHeadersMapping] = useState({
      plant_name: '',
      botanical_name: '',
      size: '',
      pot: '',
      price: '',
      availability: '',
      sku: '',
      location: ''
    });
  const [uploading, setUploading] = useState(false);
  
  // Project selection state
  const [projects, setProjects] = useState([]);
  const [showProjectPicker, setShowProjectSelection] = useState(false);
  const [selectedPlantForMasterList, setSelectedPlantForMasterList] = useState(null);
  const [addingToMasterList, setAddingToMasterList] = useState(false);

  useEffect(() => {
    fetchSuppliers();
    fetchPublicSuppliers();
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
      if (!userData?.id) return;

      const response = await fetch(`/api/projects/designer/${userData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleAddToMasterList = async (projectId) => {
    if (!selectedPlantForMasterList || !projectId) return;

    setAddingToMasterList(true);
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/plans/${projectId}/master-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plant_id: null, // Supplier plants are manual entries usually, or we match by name
          common_name: selectedPlantForMasterList.plant_name,
          botanical_name: selectedPlantForMasterList.botanical_name,
          pot_size: selectedPlantForMasterList.pot || selectedPlantForMasterList.size,
          unit_price: selectedPlantForMasterList.price,
          quantity: 1
        })
      });

      if (!response.ok) throw new Error('Failed to add to Project Plant List');
      
      alert("✅ Plant added to that project's Project Plant List!");
      setShowProjectSelection(false);
      setSelectedPlantForMasterList(null);
    } catch (error) {
      console.error('Error adding to project plant list:', error);
      alert('Failed to add plant to Project Plant List');
    } finally {
      setAddingToMasterList(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/suppliers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch suppliers');
      const data = await response.json();
      setSuppliers(data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicSuppliers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const url = searchPublicQuery 
        ? `/api/suppliers/public?city=${encodeURIComponent(searchPublicQuery)}` 
        : '/api/suppliers/public';
        
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch public suppliers');
      const data = await response.json();
      setPublicSuppliers(data.suppliers || []);
    } catch (error) {
      console.error('Error fetching public suppliers:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'discover') {
      fetchPublicSuppliers();
    }
  }, [searchPublicQuery, activeTab]);

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    if (!newSupplierName.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/suppliers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newSupplierName })
      });
      if (!response.ok) throw new Error('Failed to add supplier');
      await fetchSuppliers();
      setNewSupplierName('');
      setShowAddSupplierModal(false);
    } catch (error) {
      alert('Error adding supplier: ' + error.message);
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier and all their inventory?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete supplier');
      await fetchSuppliers();
    } catch (error) {
      alert('Error deleting supplier: ' + error.message);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseCsv(selectedFile);
    }
  };

    const parseCsv = (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        if (lines.length === 0) return;

        // More robust CSV splitting that handles quotes
        const splitCsvLine = (line) => {
          const result = [];
          let cur = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') inQuotes = !inQuotes;
            else if (char === ',' && !inQuotes) {
              result.push(cur.trim());
              cur = '';
            } else cur += char;
          }
          result.push(cur.trim());
          return result;
        };

        const detectedHeaders = splitCsvLine(lines[0]);
        setHeaders(detectedHeaders);

        const rows = lines.slice(1).map(line => {
          const values = splitCsvLine(line);
          const row = {};
          detectedHeaders.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });

        setCsvData(rows);

        // Auto-suggest mapping
        const newMapping = { ...mapping };
        detectedHeaders.forEach(header => {
          const h = header.toLowerCase().trim();
        if (h.includes('botanical') || h.includes('latin')) newMapping.botanical_name = header;
        else if (h.includes('common') || h.includes('plant') || h === 'name' || h.includes('variety')) newMapping.plant_name = header;
        else if (h === 'pot' || (h.includes('pot') && !h.includes('size'))) newMapping.pot = header;
        else if (h.includes('size') || (h.includes('pot') && h.includes('size')) || h.includes('container')) newMapping.size = header;
        else if (h.includes('price') || h.includes('cost')) newMapping.price = header;
          else if (h.includes('avail') || h.includes('stock') || h === 'qty' || h.includes('inventory')) newMapping.availability = header;
          else if (h.includes('sku') || h.includes('id') || h.includes('code')) newMapping.sku = header;
          else if (h.includes('loc')) newMapping.location = header;
        });
        setHeadersMapping(newMapping);
        setUploadStep(2);
      };
      reader.readAsText(file);
    };

  const handleUploadInventory = async () => {
    if (!selectedSupplier || !csvData.length) return;

    setUploading(true);
    try {
      const inventory = csvData.map(row => {
          const item = {
            plant_name: row[mapping.plant_name],
            botanical_name: row[mapping.botanical_name],
            size: row[mapping.size],
            pot: row[mapping.pot],
            price: parseFloat(row[mapping.price]?.replace(/[^0-9.]/g, '')) || null,
            availability: row[mapping.availability],
            sku: row[mapping.sku],
            location: row[mapping.location],
            raw_data: row
          };
        return item;
      });

      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/suppliers/${selectedSupplier.id}/inventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ inventory })
      });

      if (!response.ok) throw new Error('Failed to upload inventory');
      
      setUploadStep(3);
    } catch (error) {
      alert('Error uploading inventory: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const startWizard = (supplier) => {
    setSelectedSupplier(supplier);
    setUploadStep(1);
    setFile(null);
    setCsvData([]);
    setShowUploadWizard(true);
  };

  const closeWizard = () => {
    setShowUploadWizard(false);
    setSelectedSupplier(null);
    setUploadStep(1);
  };

  const viewInventory = async (supplier) => {
    setSelectedSupplier(supplier);
    setShowInventoryModal(true);
    setLoadingInventory(true);
    setInventorySearch('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/suppliers/${supplier.id}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch inventory');
      const data = await response.json();
      setSupplierInventory(data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      alert('Error loading inventory: ' + error.message);
    } finally {
      setLoadingInventory(false);
    }
  };

  const filteredInventory = supplierInventory.filter(item => {
    const search = inventorySearch.toLowerCase();
    return (
      (item.plant_name || '').toLowerCase().includes(search) ||
      (item.botanical_name || '').toLowerCase().includes(search) ||
      (item.sku || '').toLowerCase().includes(search)
    );
  });

  if (loading) return <div className="suppliers-container">Loading...</div>;

  return (
    <div className="suppliers-container">
      <div className="suppliers-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        <h1>Suppliers & Inventory</h1>
        <button className="primary-button" onClick={() => setShowAddSupplierModal(true)}>+ Add Private Supplier</button>
      </div>

      <div className="suppliers-tabs">
        <button 
          className={`tab-btn ${activeTab === 'my-suppliers' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-suppliers')}
        >
          My Suppliers
        </button>
        <button 
          className={`tab-btn ${activeTab === 'discover' ? 'active' : ''}`}
          onClick={() => setActiveTab('discover')}
        >
          Discover Nurseries
        </button>
      </div>

      <div className="suppliers-list">
        {activeTab === 'my-suppliers' ? (
          suppliers.length === 0 ? (
            <div className="empty-state">
              <p>No private suppliers added yet. Add your own nursery list or discover local ones.</p>
            </div>
          ) : (
            <div className="supplier-cards">
              {suppliers.map(supplier => (
                <div key={supplier.id} className="supplier-card">
                  <div className="supplier-card-header">
                    <h3>{supplier.name}</h3>
                    <div className="actions">
                      <button className="delete-btn" onClick={() => handleDeleteSupplier(supplier.id)}>🗑️</button>
                    </div>
                  </div>
                  <div className="supplier-card-body">
                    <div className="card-actions-row">
                      <button className="secondary-button" onClick={() => startWizard(supplier)}>
                        {supplier.last_upload ? 'Update Inventory' : 'Upload Inventory'}
                      </button>
                      <button className="text-button" onClick={() => viewInventory(supplier)}>
                        View/Search Inventory
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="discover-container">
            <div className="discover-search">
              <input 
                type="text" 
                placeholder="Search nurseries by city..."
                value={searchPublicQuery}
                onChange={(e) => setSearchPublicQuery(e.target.value)}
                className="discover-search-input"
              />
            </div>
            
            {publicSuppliers.length === 0 ? (
              <div className="empty-state">
                <p>No public nurseries found in your area yet.</p>
              </div>
            ) : (
              <div className="supplier-cards">
                {publicSuppliers.map(supplier => (
                  <div key={supplier.id} className="supplier-card public">
                    <div className="supplier-card-header">
                      <div className="header-info">
                        <h3>{supplier.name}</h3>
                        <span className="location-badge">{supplier.city}, {supplier.province_state}</span>
                      </div>
                    </div>
                    <div className="supplier-card-body">
                      <p className="bio">{supplier.bio || 'Professional plant supplier'}</p>
                      <div className="public-actions">
                        <button className="secondary-button" onClick={() => viewInventory(supplier)}>
                          View Inventory
                        </button>
                        {supplier.website && (
                          <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="website-link">
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

        {/* Inventory View Modal */}
        {showInventoryModal && selectedSupplier && (
          <div className="modal-overlay">
            <div className="modal-content inventory-modal">
              <div className="modal-header">
                <div className="header-title-group">
                  <h2>{selectedSupplier.name} Inventory</h2>
                  <p className="subtitle">{filteredInventory.length} items found</p>
                </div>
                <button className="close-btn" onClick={() => setShowInventoryModal(false)}>✕</button>
              </div>

              <div className="inventory-search-bar">
                <input
                  type="text"
                  placeholder="Search by plant name, botanical name, or SKU..."
                  value={inventorySearch}
                  onChange={(e) => setInventorySearch(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="inventory-table-container">
                {loadingInventory ? (
                  <div className="loading-state">Loading inventory...</div>
                ) : filteredInventory.length === 0 ? (
                  <div className="empty-state">No matching plants found in this inventory.</div>
                ) : (
                  <table className="inventory-table">
                    <thead>
                      <tr>
                        <th>Plant Name</th>
                        <th>Botanical Name</th>
                        <th>Pot</th>
                        <th>Size</th>
                        <th>Price</th>
                        <th>Availability</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.plant_name || '-'}</td>
                          <td className="italic">{item.botanical_name || '-'}</td>
                          <td>{item.pot || '-'}</td>
                          <td>{item.size || '-'}</td>
                          <td className="price">{item.price ? `$${item.price}` : '-'}</td>
                          <td>
                            <span className={`status-pill ${item.availability?.toLowerCase().includes('stock') || item.availability?.toLowerCase().includes('yes') ? 'in-stock' : ''}`}>
                              {item.availability || '-'}
                            </span>
                          </td>
                          <td>
                            <button 
                              className="add-to-master-btn"
                              onClick={() => {
                                setSelectedPlantForMasterList(item);
                                setShowProjectSelection(true);
                              }}
                              title="Add to a project's Project Plant List"
                            >
                              📋+
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Supplier Modal */}
      {showAddSupplierModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add New Supplier</h3>
            <form onSubmit={handleAddSupplier}>
              <div className="form-group">
                <label>Supplier Name</label>
                <input 
                  type="text" 
                  value={newSupplierName} 
                  onChange={(e) => setNewSupplierName(e.target.value)} 
                  placeholder="e.g. ABC Nursery"
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddSupplierModal(false)}>Cancel</button>
                <button type="submit" className="primary-button" disabled={!newSupplierName.trim()}>Add Supplier</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Wizard Modal */}
      {showUploadWizard && (
        <div className="modal-overlay">
          <div className="modal-content wizard-modal">
            <div className="wizard-header">
              <h3>Upload Inventory: {selectedSupplier.name}</h3>
              <button className="close-btn" onClick={closeWizard}>✕</button>
            </div>

            <div className="wizard-steps">
              <div className={`step ${uploadStep >= 1 ? 'active' : ''}`}>1. Upload</div>
              <div className={`step ${uploadStep >= 2 ? 'active' : ''}`}>2. Map Fields</div>
              <div className={`step ${uploadStep >= 3 ? 'active' : ''}`}>3. Finish</div>
            </div>

            <div className="wizard-body">
              {uploadStep === 1 && (
                <div className="upload-step">
                  <p>Upload a CSV file containing your supplier's plant inventory.</p>
                  <input type="file" accept=".csv" onChange={handleFileChange} />
                </div>
              )}

              {uploadStep === 2 && (
                <div className="mapping-step">
                  <p>Match the columns from your file to our system fields.</p>
                  <div className="mapping-grid">
                    {Object.keys(mapping).map(field => (
                      <div key={field} className="mapping-row">
                        <label>{field.replace('_', ' ').toUpperCase()}</label>
                        <select 
                          value={mapping[field]} 
                          onChange={(e) => setHeadersMapping({ ...mapping, [field]: e.target.value })}
                        >
                          <option value="">-- Skip Field --</option>
                          {headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  
                  <div className="preview-table-container">
                    <h4>Raw Data Preview (first 3 rows)</h4>
                    <p className="text-xs text-gray-500 mb-2 italic">Verify that your columns are aligned correctly below.</p>
                    <table className="preview-table">
                      <thead>
                        <tr>
                          {headers.map(header => (
                            <th key={header}>{header.toUpperCase()}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0, 3).map((row, i) => (
                          <tr key={i}>
                          {headers.map(header => <td key={header}>{row[header]}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              )}

              {uploadStep === 3 && (
                <div className="success-step">
                  <div className="success-icon">✅</div>
                  <p>Inventory uploaded successfully!</p>
                  <p>{csvData.length} plants are now available for matching.</p>
                </div>
              )}
            </div>

            <div className="wizard-footer">
              {uploadStep === 2 && (
                <button className="primary-button" onClick={handleUploadInventory} disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Confirm & Save Inventory'}
                </button>
              )}
              {uploadStep === 3 && (
                <button className="primary-button" onClick={closeWizard}>Done</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Project Selection Modal */}
      {showProjectPicker && (
        <div className="modal-overlay">
          <div className="modal-content project-picker-modal">
            <div className="modal-header">
              <h3>Select Project</h3>
              <button className="close-btn" onClick={() => setShowProjectSelection(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="mb-4">Which project&apos;s <strong>Project Plant List</strong> should this plant be added to?</p>
              <div className="project-list">
                {projects.length === 0 ? (
                  <p className="empty-state">No active projects found.</p>
                ) : (
                  projects.map(project => (
                    <button 
                      key={project.id} 
                      className="project-option"
                      onClick={() => handleAddToMasterList(project.id)}
                      disabled={addingToMasterList}
                    >
                      <div className="project-info">
                        <span className="project-name">{project.name}</span>
                        <span className="project-client">{project.client_name || 'No client'}</span>
                      </div>
                      {addingToMasterList ? '⏳' : '➔'}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Suppliers;
